import React, { PropsWithChildren, useMemo } from "react";
import { setContext } from "@apollo/client/link/context";
import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from "@apollo/client";

interface CognitoSession {
  getAccessToken: () => { getJwtToken: () => string } | undefined;
}

export interface TicketMasterApolloClientProps {
  apolloClient?: {
    uri?: string;
    context?: string;
    token?: string;
    getCurrentSession?: () => Promise<CognitoSession>;
  };
}

const TicketMasterApolloClient: React.FC<
  PropsWithChildren<TicketMasterApolloClientProps>
> = ({ apolloClient, children }) => {
  const client = useMemo<ApolloClient<NormalizedCacheObject> | undefined>(() => {
    if (!!apolloClient) {
      const httpLink = createHttpLink({
        uri: apolloClient?.uri,
      });

      const setAuthorizationLink = setContext((request) => {
        const headersObj: { headers: Record<string, string> } = {
          headers: {},
        };

        if (apolloClient?.context) {
          headersObj.headers["X-Account"] = apolloClient?.context;
        }

        if (apolloClient?.getCurrentSession) {
          return apolloClient
            ?.getCurrentSession()
            .then((data) => {
              const token = data?.getAccessToken()?.getJwtToken();

              headersObj.headers.Authorization = `Bearer ${token}`;
              return headersObj;
            })
            .catch((err) => {
              console.log("Error getting user token ", err);
              return request;
            });
        } else {
          headersObj.headers.Authorization = `Bearer ${apolloClient?.token}`;

          return headersObj;
        }
      });

      return new ApolloClient({
        link: setAuthorizationLink.concat(httpLink),
        cache: new InMemoryCache({
          typePolicies: {
            KeyValuePair: { keyFields: ["id", "name"] },
          },
        }),
      });
    }
  }, [
    apolloClient?.uri,
    apolloClient?.context,
    apolloClient?.token,
    apolloClient?.getCurrentSession,
  ]);

  return (
    // client is `ApolloClient<...> | undefined`; ApolloProvider's prop is
    // non-optional. Original runtime passed undefined when apolloClient was
    // falsy (latent-crash flag already logged); cast preserves that behavior.
    <ApolloProvider client={client as ApolloClient<NormalizedCacheObject>}>
      {children}
    </ApolloProvider>
  );
};

export default TicketMasterApolloClient;
